import React, { useState, useRef, useEffect, useCallback } from "react";
import { connect } from 'react-redux';
import { Formik } from 'formik';

import { Button, SVGIcon, Label } from '../../../../components/custom-essentials';
import { BrowserTabTitle, LoadingOverlay, TooltipWrapper } from '../../../../components/display';
import { FormikControl, Check, checkResponses } from '../../../../components/form';
import { getDataArray, exportToCSV } from '../../../../components/functions';
import { Table } from '../../../../components/table';
import { Socket } from '../../../../components/ws';
import CycleGroupModal from './CycleGroupModal';

import {
    fetchCycleGroupsAll,
    fetchRpCentersAll,
    fetchStudentsAll,
    fetchContractsActiveStudents,
    createCycleGroup,
    updateCycleGroup,
    deleteCycleGroup,
} from '../../../../actions';

// Detect portal - use "Group Manager" for ReadingPlex, "Cycle Groups" for MathPlex
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const isReadingPlex = hostname.includes('readingplex');
const pageTitle = isReadingPlex ? 'Group Manager' : 'Cycle Groups';

const defaultSorted = { dataField: 'name', order: 'asc' };

function CycleGroups(props) {
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => (mounted.current = false);
    });
    const formRef = useRef();

    const [hasLoaded, setHasLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState(false);
    
    const [groups, setGroups] = useState([]);
    const [groupMembers, setGroupMembers] = useState([]);
    const [filteredGroups, setFilteredGroups] = useState([]);
    const [students, setStudents] = useState([]);
    const [centers, setCenters] = useState([]);
    const [contracts, setContracts] = useState([]);

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedGroup, setSelectedGroup] = useState(null);

    const { fetchCycleGroupsAll, fetchRpCentersAll, fetchStudentsAll,
        fetchContractsActiveStudents, createCycleGroup, updateCycleGroup, deleteCycleGroup } = props;

    const handleOpenModal = (mode, group = null) => {
        setModalMode(mode);
        setSelectedGroup(group);
        setShowModal(true);
    };

    const handleCloseModal = (shouldRefresh = false) => {
        setShowModal(false);
        setSelectedGroup(null);
        if (shouldRefresh) {
            refreshData();
        }
    };

    const filterGroups = useCallback((unfilteredGroups, filterQuery, hideInactive = false) => {
        let filtered = [...unfilteredGroups];

        // Filter by inactive status
        if (hideInactive) {
            filtered = filtered.filter(g => parseInt(g.active) === 1);
        }

        // Filter by search query
        if (filterQuery && filterQuery.trim() !== '') {
        const query = filterQuery.toLowerCase().trim();
            filtered = filtered.filter(g => {
            return (
                (g.name && g.name.toLowerCase().includes(query)) ||
                    (g.notes && g.notes.toLowerCase().includes(query)) ||
                    (g.centerName && g.centerName.toLowerCase().includes(query)) ||
                    (g.memberNames && g.memberNames.toLowerCase().includes(query))
            );
        });
        }

        setFilteredGroups(filtered);
    }, []);

    // Export to CSV function
    const handleExportCSV = () => {
        const csvData = filteredGroups.map(g => ({
            'Group Name': g.name || '',
            'Center': g.centerName || '',
            'Students': g.memberNames || '',
            'Notes': g.notes || '',
            'Scheduling Info': g.scheduling_info || '',
            'Active': parseInt(g.active) === 1 ? 'Active' : 'Inactive'
        }));
        exportToCSV(csvData, `${pageTitle.replace(' ', '_')}_Export`);
    };

    const refreshData = useCallback(() => {
        (async function refresh() {
            if (loading || !formRef.current?.values) return;
            if (mounted.current) setLoading(true);

            try {
                const groupsRes = await fetchCycleGroupsAll();
                const centersRes = await fetchRpCentersAll();
                const studentsRes = await fetchStudentsAll();

                // Fetch contracts for ReadingPlex
                let contractsRes = { data: [] };
                if (isReadingPlex) {
                    try {
                        contractsRes = await fetchContractsActiveStudents();
                    } catch (e) {
                        console.log('[GROUP MANAGER] Error fetching contracts:', e);
                    }
                }
                
                const isApiError = checkResponses(groupsRes, centersRes, studentsRes);
                if (isApiError) {
                    if (mounted.current) {
                        setApiError('Error fetching data from the server. Please try again later.');
                        setLoading(false);
                        setHasLoaded(true);
                    }
                    return;
                } else setApiError(false);

                // Extract groups and groupMembers from response
                const groupsData = groupsRes.data || {};
                const newGroups = Array.isArray(groupsData.groups) ? groupsData.groups : [];
                const newGroupMembers = Array.isArray(groupsData.groupMembers) ? groupsData.groupMembers : [];
                
                const newCenters = getDataArray(centersRes);
                const newStudents = getDataArray(studentsRes);

                // Extract contracts
                let newContracts = [];
                if (Array.isArray(contractsRes?.data)) {
                    newContracts = contractsRes.data;
                } else if (Array.isArray(contractsRes?.data?.contracts)) {
                    newContracts = contractsRes.data.contracts;
                }

                // Create maps for lookups
                const centerMap = {};
                newCenters.forEach(c => centerMap[parseInt(c.id)] = c.name);
                
                const studentMap = {};
                newStudents.forEach(s => studentMap[s.user_id] = `${s.first_name} ${s.last_name}`);

                // Append center names and member counts to groups
                const groupsAppended = newGroups.map(g => {
                    g.centerName = centerMap[parseInt(g.center)] || `Unknown center (ID: ${g.center})`;
                    g.memberCount = newGroupMembers.filter(gm => parseInt(gm.group_id) === parseInt(g.id)).length;
                    g.memberNames = newGroupMembers
                        .filter(gm => parseInt(gm.group_id) === parseInt(g.id))
                        .map(gm => {
                            // Backend uses 'student' field, not 'user_id'
                            const studentId = gm.student || gm.user_id;
                            return studentMap[studentId] || `Unknown student (ID: ${studentId || 'undefined'})`;
                        })
                        .join(', ');
                    return g;
                });

                const { filterQuery, hideInactive } = formRef.current.values;

                if (mounted.current) {
                    setGroups(groupsAppended);
                    setGroupMembers(newGroupMembers);
                    setCenters(newCenters);
                    setStudents(newStudents);
                    setContracts(newContracts);
                    filterGroups(groupsAppended, filterQuery, hideInactive);
                    setHasLoaded(true);
                    setLoading(false);
                }
            } catch (error) {
                console.error('[CYCLE GROUPS] Error:', error);
                if (mounted.current) {
                    setApiError('Error fetching data. Please try again later.');
                    setLoading(false);
                    setHasLoaded(true);
                }
            }
        })();
    }, [mounted, loading, fetchCycleGroupsAll, fetchRpCentersAll, fetchStudentsAll, filterGroups]);

    useEffect(() => {
        refreshData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Columns per Guide Page 7: Group Name, Center, Students, Notes, Scheduling Info, Active, Actions
    const getColumns = () => [
        {
            dataField: 'name',
            text: 'Group Name',
            sort: true,
            formatter: (row) => (
                <span className="font-semibold">{row.name}</span>
            )
        },
        {
            dataField: 'centerName',
            text: 'Center',
            sort: true,
            formatter: (row) => (
                <Label color={row.centerName?.toLowerCase().includes('online') ? 'mpLBlue' : 'mpGreen'}>
                    {row.centerName || 'Unknown'}
                </Label>
            )
        },
        {
            dataField: 'memberNames',
            text: 'Students',
            formatter: (row) => (
                <div className="text-sm">
                    {row.memberNames || <span className="text-gray-400">No students</span>}
                </div>
            )
        },
        {
            dataField: 'notes',
            text: 'Notes',
            formatter: (row) => (
                <div className="text-sm">
                    {row.notes ? (
                        <span>{row.notes}</span>
                    ) : (
                        <Label color="mpGray">None</Label>
                    )}
                </div>
            )
        },
        {
            dataField: 'scheduling_info',
            text: 'Scheduling Info',
            formatter: (row) => {
                // Format: Dates: MM/DD/YYYY to MM/DD/YYYY, Time: HH:MM PM, Days: X
                const parts = [];
                if (row.start_date && row.end_date) {
                    // Format dates as MM/DD/YYYY
                    const formatDateDisplay = (dateStr) => {
                        if (!dateStr) return '';
                        try {
                            const d = new Date(dateStr);
                            if (isNaN(d.getTime())) return dateStr;
                            const month = (d.getMonth() + 1).toString().padStart(2, '0');
                            const day = d.getDate().toString().padStart(2, '0');
                            const year = d.getFullYear();
                            return `${month}/${day}/${year}`;
                        } catch (e) {
                            return dateStr;
                        }
                    };
                    parts.push(`Dates: ${formatDateDisplay(row.start_date)} to ${formatDateDisplay(row.end_date)}`);
                }
                if (row.time !== undefined && row.time !== null) {
                    // Format time from minutes since midnight to HH:MM AM/PM
                    const timeVal = parseInt(row.time);
                    if (!isNaN(timeVal)) {
                        const hours = Math.floor(timeVal / 60);
                        const minutes = (timeVal % 60).toString().padStart(2, '0');
                        const ampm = hours >= 12 ? 'PM' : 'AM';
                        const displayHours = hours > 12 ? hours - 12 : (hours === 0 ? 12 : hours);
                        parts.push(`Time: ${displayHours}:${minutes} ${ampm}`);
                    }
                } else if (row.meeting_time) {
                    parts.push(`Time: ${row.meeting_time}`);
                }
                if (row.meeting_days) {
                    parts.push(`Days: ${row.meeting_days}`);
                } else {
                    // Build days from individual day flags
                    const days = [];
                    if (parseInt(row.sundays) === 1) days.push('Sun');
                    if (parseInt(row.mondays) === 1) days.push('Mon');
                    if (parseInt(row.tuesdays) === 1) days.push('Tue');
                    if (parseInt(row.wednesdays) === 1) days.push('Wed');
                    if (parseInt(row.thursdays) === 1) days.push('Thu');
                    if (parseInt(row.fridays) === 1) days.push('Fri');
                    if (parseInt(row.saturdays) === 1) days.push('Sat');
                    if (days.length > 0) {
                        parts.push(`Days: ${days.join(', ')}`);
                    }
                }
                return (
                    <div className="text-xs">
                        {parts.length > 0 ? parts.map((p, i) => (
                            <div key={i}>{p}</div>
                        )) : <span className="text-gray-400">Not set</span>}
                    </div>
                );
            }
        },
        {
            dataField: 'active',
            text: 'Active',
            sort: true,
            formatter: (row) => (
                <Label color={parseInt(row.active) === 1 ? 'mpGreen' : 'mpLRed'}>
                    {parseInt(row.active) === 1 ? 'Active' : 'Inactive'}
                </Label>
            )
        },
        {
            dataField: 'actions',
            text: 'Actions',
            formatter: (row) => (
                <div className="flex flex-row gap-x-2">
                    <TooltipWrapper tooltipText="Edit group">
                        <Button
                            color="lte-mpLBlue"
                            variant="icon"
                            onClick={() => handleOpenModal('edit', row)}
                        >
                            <SVGIcon fileName="pencil" />
                        </Button>
                    </TooltipWrapper>
                    <TooltipWrapper tooltipText="Delete group">
                        <Button
                            color="lte-mpLRed"
                            variant="icon"
                            onClick={() => handleOpenModal('delete', row)}
                        >
                            <SVGIcon fileName="trash" />
                        </Button>
                    </TooltipWrapper>
                </div>
            )
        }
    ];

    return (
        <div className="page-box">
            <BrowserTabTitle>{pageTitle}</BrowserTabTitle>
            {loading && <LoadingOverlay />}
            <div className="card">
                <Formik
                    enableReinitialize
                    initialValues={{
                        filterQuery: '',
                        hideInactive: true
                    }}
                    innerRef={formRef}
                    onSubmit={() => null}
                >
                    {formik => (
                        <form onSubmit={formik.handleSubmit}>
                            <h2>{pageTitle}</h2>
                            <br />

                            {/* Button row - per Guide Page 7 */}
                            <div className="flex flex-row gap-x-2 mb-4">
                                <Button
                                    color="lte-mpOrange"
                                    onClick={() => refreshData()}
                                >
                                    Refresh
                                </Button>
                                <Button
                                    color="lte-mpLBlue"
                                    onClick={() => handleOpenModal('create')}
                                >
                                    + Add Group
                                </Button>
                                <div className="flex-grow" />
                                <Button
                                    color="lte-mpTeal"
                                    onClick={handleExportCSV}
                                >
                                    Export Groups to CSV
                                </Button>
                            </div>

                            {/* Filter row */}
                            <div className="mb-4">
                                <h4>Filter Results</h4>
                                <div className="flex flex-row gap-x-4 items-center">
                                    <div className="w-32">
                                        <label className="text-sm">Center</label>
                                        {/* Could add center dropdown here */}
                                    </div>
                                    <div className="flex-grow">
                                    <FormikControl
                                        id="cycle-groups-searchQuery"
                                        name="filterQuery"
                                        placeholder="Search groups..."
                                        value={formik.values.filterQuery}
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                                filterGroups(groups, e.target.value, formik.values.hideInactive);
                                            }}
                                        />
                                    </div>
                                    <div className="flex items-center">
                                        <Check
                                            id="cycle-groups-hideInactive"
                                            name="hideInactive"
                                            color="mpLBlue"
                                            label="Hide Inactive Groups"
                                            checked={formik.values.hideInactive}
                                            onChange={(e) => {
                                                formik.handleChange(e);
                                                filterGroups(groups, formik.values.filterQuery, e.target.checked);
                                        }}
                                    />
                                    </div>
                                </div>
                            </div>

                            {apiError ? <div className="text-mpLRed">{apiError}</div> :
                                hasLoaded &&
                                <Table
                                    tableName="cycleGroups"
                                    data={filteredGroups}
                                    columns={getColumns()}
                                    defaultSorted={defaultSorted}
                                    emptyMessage="No cycle groups found"
                                />
                            }
                        </form>
                    )}
                </Formik>
            </div>

            <Socket
                refreshData={refreshData}
                page={pageTitle}
                setVersion={props.setVersion}
            />

            {showModal && (
                <CycleGroupModal
                    key={`group-modal-${modalMode}-${selectedGroup?.id || 'new'}`}
                    show={showModal}
                    mode={modalMode}
                    group={selectedGroup}
                    centers={centers}
                    students={students}
                    groupMembers={groupMembers}
                    contracts={contracts}
                    onClose={handleCloseModal}
                    createCycleGroup={createCycleGroup}
                    updateCycleGroup={updateCycleGroup}
                    deleteCycleGroup={deleteCycleGroup}
                />
            )}
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        auth: state.auth
    };
}

export default connect(mapStateToProps, {
    fetchCycleGroupsAll,
    fetchRpCentersAll,
    fetchStudentsAll,
    fetchContractsActiveStudents,
    createCycleGroup,
    updateCycleGroup,
    deleteCycleGroup
})(CycleGroups);

